import "./index.css";
import "./index.scss";
import "@app/shared/lib/dayjs.ts";

import { joinURL } from "@app/shared/lib/Functions/urls.ts";
import { setupPwaInstallListener } from "@features/pwa-install/model/pwaInstallStore.ts";
import log from "@fxid/shared";
import { AppConfig } from "@fxid/shared";
import * as Sentry from "@sentry/react";
import { BrowserOptions } from "@sentry/react";
import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, HashRouter } from "react-router-dom";

import App from "./App.tsx";

// globalThis.log = log;

// Применяем конфигурацию уровня логирования из переменных окружения
log.setDefaultLevel(
	import.meta.env.VITE_APP_ENV_OVERRIDE_NAME === "prod"
		? "warn"
		: import.meta.env.VITE_APP_ENV_OVERRIDE_NAME === "dev"
			? "trace"
			: "debug"
);

// Вызываем как можно раньше подпись на PWA события, а то прилетят и вжух!
setupPwaInstallListener();

log.info(`Environment: %o`, import.meta.env);
log.info(`routerBasePath: %s`, AppConfig.routerBasePath);
log.info(`apiBasePath: %s`, AppConfig.apiBasePath);
log.info(`BuildId: %s`, import.meta.env.VITE_APP_BUILD_ID);

// --- Sentry Initialization ---

// Функция для инициализации Sentry
const initializeSentry = () => {
	// Sentry tunnel должен идти на fxid API (AppConfig.apiBasePath), а НЕ на BASE_URL (базу ассетов).
	// В FB Instant паке BASE_URL=./ и приложение хостится на apps-*.apps.fbsbx.com: относительный
	// tunnel уходил на CDN фейсбука (400), и ни одно событие из пака не доходило до Sentry.
	// apiBasePath в паке — абсолютный origin API игры (FX_CONFIG), в обычном вебе — свой origin. #fxid-1798
	const sentryUrl = joinURL(AppConfig.apiBasePath, "/api/s_tunnel");

	const integrations: Sentry.BrowserOptions["integrations"] = [
		Sentry.browserTracingIntegration(),
		Sentry.replayIntegration({
			maskAllText: false,
			blockAllMedia: false
		})
	];

	// Составное окружение для Sentry: {tenant}-{env}. Та же формула у бэкенда (SentryExtensions.SentryEnv),
	// fxid-launcher и deploy-записей Jenkins — события одной выкатки фильтруются одним environment
	const sentryTenant = String(import.meta.env.VITE_APP_TENANT_OVERRIDE ?? "default").toLowerCase();
	const sentryEnvRaw = String(import.meta.env.VITE_APP_ENV_OVERRIDE_NAME ?? "");
	const sentryEnvironment = `${sentryTenant}-${sentryEnvRaw}`;

	const sentryInitConfig: BrowserOptions = {
		release: import.meta.env.VITE_APP_BUILD_ID,
		environment: sentryEnvironment,
		dsn: "https://7cb05911025b9bc5fc5c5182ca0542d4@sentry.fxgam.es/4",
		tunnel: sentryUrl,
		integrations: integrations,
		// Performance Monitoring
		tracesSampleRate: 0.25, //  Capture 25% of the transactions
		// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
		// tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
		// Session Replay
		replaysSessionSampleRate: import.meta.env.VITE_APP_ENV_OVERRIDE_NAME === "prod" ? 0.1 : 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
		replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
		initialScope: {
			tags: {
				tenant: sentryTenant,
				env: sentryEnvRaw
			}
		}
	};

	log.info("Initializing sentry with config: %o", sentryInitConfig);

	Sentry.init(sentryInitConfig);
};

// Проверяем, есть ли в URL параметр `noSentry`, чтобы отключить инициализацию Sentry для отладки
const urlParams = new URLSearchParams(window.location.search);
const isSentryDisabled = urlParams.has("noSentry");

if (!isSentryDisabled) {
	initializeSentry();
} else {
	log.warn("Sentry initialization skipped due to 'noSentry' query parameter");
}

// --- End of Sentry Initialization ---

// Функция, которая предотвращает вызов контекстного меню
function disableContextMenu(event: MouseEvent) {
	event.preventDefault();
}

// Назначение обработчика события на весь документ и окно
document.addEventListener("contextmenu", disableContextMenu);
window.addEventListener("contextmenu", disableContextMenu);

const container = document.getElementById("root");
const root = createRoot(container!);

const Router = AppConfig.routerType == "HashRouter" ? HashRouter : BrowserRouter;

root.render(
	<Router basename={AppConfig.routerBasePath}>
		<App />
	</Router>
);

if (import.meta.env.VITE_APP_NO_SW != null) {
	log.info("SW disabled");
} else {
	if ("serviceWorker" in navigator) {
		log.info("SW registering");

		navigator.serviceWorker
			.register(import.meta.env.PROD ? "/service-worker.js" : "/dev-sw.js?dev-sw", {
				type: import.meta.env.MODE === "production" ? "classic" : "module"
			})
			.then(log.info)
			.catch(log.error);
	}
}
